/**
 * 资金对账与差错处理 API（ADM-14）。
 *
 * 覆盖对账运行、差错单列表/详情/处理与资金健康告警查询；
 * 写操作的幂等键由页面在单次处理意图内生成并复用。
 */
import { ApiPaths } from "./api.config";
import type { Paginated } from "./campuses.api";
import { http } from "./transport";

export type FundExceptionType =
  | "LOST_PAYMENT"
  | "LATE_PAYMENT"
  | "DUPLICATE_PAYMENT"
  | "AMOUNT_MISMATCH"
  | "PAID_NOT_ENTITLED"
  | "OWNERSHIP_ANOMALY";

export type FundExceptionStatus =
  "PENDING" | "PROCESSING" | "CLOSED" | "FAILED";

export type RecommendedAction =
  | "GRANT_ENTITLEMENT"
  | "REFUND"
  | "CORRECT_AMOUNT"
  | "REVERSE_ENTITLEMENT"
  | "RETRY"
  | "CLOSE";

export type FundExceptionActionResult = "SUCCEEDED" | "FAILED" | "REJECTED";

/** 差错单列表筛选。 */
export interface FundExceptionListQuery {
  exceptionType?: FundExceptionType;
  campusId?: string;
  orderNo?: string;
  studentId?: string;
  status?: FundExceptionStatus;
  discoveredFrom?: string;
  discoveredTo?: string;
  page?: number;
  pageSize?: number;
}

/** 差错单列表行。 */
export interface FundExceptionItem {
  exceptionId: string;
  exceptionNo: string;
  exceptionType: FundExceptionType;
  businessKey: string;
  orderId: string | null;
  orderNo: string | null;
  studentId: string | null;
  studentName: string | null;
  campusId: string | null;
  differenceAmountMinor: number;
  channelStatus: string;
  localStatus: string;
  recommendedAction: RecommendedAction;
  status: FundExceptionStatus;
  lastObservedAt: string;
  createdAt: string;
}

/** 渠道侧证据快照。 */
export interface ChannelEvidence {
  platformTxnId: number;
  outTradeNo: string | null;
  channelAmountMinor: number;
  billDate: string;
  completedAt: string;
  paymentMethod?: string;
}

/** 本地侧证据引用。 */
export interface LocalEvidenceRefs {
  paymentTransactionId: string | null;
  rawPaymentEventId: string | null;
  wechatPaymentSessionId: string | null;
  orderId: string | null;
  orderStatus: string | null;
  localAmountMinor: number | null;
  rawEventStatus: string | null;
}

/** 差错单处理轨迹。 */
export interface FundExceptionAction {
  actionId: string;
  action: RecommendedAction;
  result: FundExceptionActionResult;
  operatorId: string | null;
  reason: string | null;
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  traceId: string | null;
  performedAt: string;
}

/** 差错单详情。 */
export interface FundExceptionDetail extends FundExceptionItem {
  channelEvidence: ChannelEvidence;
  localEvidenceRefs: LocalEvidenceRefs;
  actions: FundExceptionAction[];
}

/** 差错单处理请求。 */
export interface ResolveFundExceptionPayload {
  action: RecommendedAction;
  reason: string;
  idempotencyKey: string;
}

/** 差错单处理结果视图。 */
export interface ResolveFundExceptionResult {
  exceptionId: string;
  exceptionStatus: FundExceptionStatus;
  action: RecommendedAction;
  result: FundExceptionActionResult;
  message: string | null;
  idempotentHit: boolean;
}

/** 对账运行记录筛选。 */
export interface ReconciliationRunListQuery {
  billDate?: string;
  status?: "RUNNING" | "SUCCEEDED" | "FAILED";
  triggerSource?: "SCHEDULED" | "MANUAL";
  page?: number;
  pageSize?: number;
}

/** 对账运行记录。 */
export interface ReconciliationRun {
  runId: string;
  billDate: string;
  triggerSource: "SCHEDULED" | "MANUAL";
  status: "RUNNING" | "SUCCEEDED" | "FAILED";
  platformEntryCount: number;
  matchedCount: number;
  ignoredCount: number;
  differenceCount: number;
  errorInfo: { code: string; message: string } | null;
  startedAt: string;
  completedAt: string | null;
}

/** 手动补跑对账请求。 */
export interface CreateReconciliationRunPayload {
  billDate: string;
}

/** 手动补跑结果。 */
export interface CreateReconciliationRunResult extends ReconciliationRun {
  idempotentHit: boolean;
}

/** 资金健康告警筛选。 */
export interface FundAlertListQuery {
  metric?:
    | "WECHAT_PAYMENT_SUCCESS_RATE"
    | "CALLBACK_DELAY_BREACH"
    | "ORDER_BACKLOG"
    | "RECON_DIFFERENCE";
  status?: "ACTIVE" | "RESOLVED";
  triggeredFrom?: string;
  triggeredTo?: string;
  page?: number;
  pageSize?: number;
}

/** 资金健康告警行。 */
export interface FundAlertItem {
  alertId: string;
  metric:
    | "WECHAT_PAYMENT_SUCCESS_RATE"
    | "CALLBACK_DELAY_BREACH"
    | "ORDER_BACKLOG"
    | "RECON_DIFFERENCE";
  metricLabel: string;
  observedValue: number;
  lastObservedValue: number | null;
  thresholdValue: number;
  severity: "WARNING" | "CRITICAL";
  severityLabel: string;
  status: "ACTIVE" | "RESOLVED";
  statusLabel: string;
  triggeredAt: string;
  resolvedAt: string | null;
  notificationStatus: "PENDING" | "SENT" | "FAILED";
  notificationStatusLabel: string;
}

function buildQuery<TQuery extends object>(
  path: string,
  query: TQuery,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
}

async function listReconciliationRuns(
  query: ReconciliationRunListQuery = {},
): Promise<Paginated<ReconciliationRun>> {
  return http.get<Paginated<ReconciliationRun>>(
    buildQuery(ApiPaths.reconciliationRuns.list, query),
  );
}

async function createReconciliationRun(
  payload: CreateReconciliationRunPayload,
): Promise<CreateReconciliationRunResult> {
  return http.post<CreateReconciliationRunResult>(
    ApiPaths.reconciliationRuns.create,
    payload,
  );
}

async function listFundExceptions(
  query: FundExceptionListQuery = {},
): Promise<Paginated<FundExceptionItem>> {
  return http.get<Paginated<FundExceptionItem>>(
    buildQuery(ApiPaths.fundExceptions.list, query),
  );
}

async function getFundException(id: string): Promise<FundExceptionDetail> {
  return http.get<FundExceptionDetail>(ApiPaths.fundExceptions.detail(id));
}

async function resolveFundException(
  id: string,
  payload: ResolveFundExceptionPayload,
): Promise<ResolveFundExceptionResult> {
  return http.post<ResolveFundExceptionResult>(
    ApiPaths.fundExceptions.resolve(id),
    payload,
  );
}

async function listFundAlerts(
  query: FundAlertListQuery = {},
): Promise<Paginated<FundAlertItem>> {
  return http.get<Paginated<FundAlertItem>>(
    buildQuery(ApiPaths.fundsAlerts.list, query),
  );
}

/** 资金异常 API 唯一对外入口。 */
export const fundExceptionsApi = {
  listReconciliationRuns,
  createReconciliationRun,
  listFundExceptions,
  getFundException,
  resolveFundException,
  listFundAlerts,
};
